import flatMap from 'lodash/flatMap';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';

import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import {
  FIELD_ID,
  FILTERS_TYPE,
  FILTERS_TYPE_BY_FIELD_ID,
  GROUP_ID,
  MAX_NUM_SHOWN_FILTERS_IN_CONDENSED_VIEW
} from 'app/constants/BrowseConstants';
import { serializeFieldId as serializeFieldIdForBrowseQuerystring } from 'app/helpers/browse/browseQueryHelpers';

const makeFieldLabel = (intl, { fieldId, isPlural = false }) => {
  let targetFieldId = fieldId;
  if (fieldId === FIELD_ID.PRIMARY_LANGUAGE) {
    targetFieldId = FIELD_ID.LANGUAGE;
  }

  return intl.formatMessage({
    defaultMessage: targetFieldId,
    id: `${targetFieldId}.field${isPlural ? '.plural' : ''}`
  });
};

const makeFilterLabel = (intl, { fieldId, filterId, filterLabel, numResults }) => {
  let labelBeforeNumResults = '';

  if (fieldId === FIELD_ID.AUTHOR) {
    labelBeforeNumResults = filterId;
  } else if (fieldId === FIELD_ID.SCOPE) {
    labelBeforeNumResults = filterLabel;
  } else if (fieldId === FIELD_ID.PRIMARY_LANGUAGE) {
    labelBeforeNumResults = intl.formatMessage({
      defaultMessage: filterId,
      id: `${FIELD_ID.LANGUAGE}.${filterId.toLowerCase()}`
    });
  } else {
    labelBeforeNumResults = intl.formatMessage({
      defaultMessage: filterId,
      id: `${fieldId}.${filterId.toLowerCase()}`
    });
  }

  const isShowNumResults = Number.isInteger(numResults);

  return `${labelBeforeNumResults}${isShowNumResults ? ` (${numResults})` : ''}`;
};

const makeGroupLabel = (intl, { fieldId, groupId }) => {
  if (fieldId === FIELD_ID.FORMAT) {
    return intl.formatMessage({ id: `SUPERFORMAT.${groupId.toLowerCase()}` });
  }

  return groupId;
};

// TODO: tests?
const makeTabLinkUrl = ({ path, searchParams }) => {
  const destinationSearchParams = new URLSearchParams();
  const serializedScopeFieldKey = serializeFieldIdForBrowseQuerystring(FIELD_ID.SCOPE);
  const searchScope = searchParams.get(serializedScopeFieldKey);

  if (searchScope) {
    destinationSearchParams.append(serializedScopeFieldKey, searchScope);
  }

  const querystring = destinationSearchParams.toString();

  return path.concat(querystring.length ? `?${querystring}` : '');
};

const getNumAppliedFilters = (fieldId, groups) => {
  if (fieldId === FIELD_ID.SCOPE) {
    return 0;
  }

  const uniqueFilters = uniqBy(
    flatMap(groups, group => group.filters),
    'filterId'
  );

  return uniqueFilters.filter(filter => filter.isApplied).length;
};

const getUniqueAppliedFilters = fields => {
  return uniqBy(
    flatMap(fields, field => {
      if (field.fieldId === FIELD_ID.SCOPE) {
        return [];
      }

      return flatMap(field.groups, group => {
        return group.filters.filter(filter => filter.isApplied);
      });
    }),
    'filterId'
  );
};

const mapCategoriesToNavRowLinks = categories => {
  return categories.map(category => {
    const { path, title } = category;

    return {
      isRouterLink: true,
      title,
      url: `/v2/browse${path}`
    };
  });
};

const mapCatalogSearchFieldsToFacetFields = (
  catalogSearchFields,
  {
    formatFieldLabel,
    formatFilterLabel,
    formatGroupLabel,
    formatShowMoreOverlayTitle,
    orderedFieldIds,
    queryState,
    renderAllLanguagesToggle,
    searchScopes = [],
    showMoreFieldId = null,
    wasLanguageFieldToggled
  }
) => {
  const { appliedFilterIdsByFieldId, isIncludeAllLanguages } = queryState;

  const catalogSearchFieldById = keyBy(catalogSearchFields, field => field.id);

  return orderedFieldIds
    .map(fieldId => {
      const isScopeField = fieldId === FIELD_ID.SCOPE;

      if (isScopeField) {
        const hasManyScopes = searchScopes.length > 1;

        if (hasManyScopes) {
          return {
            id: FIELD_ID.SCOPE,
            hasMore: false,
            fieldFilters: searchScopes.map(scope => {
              const { scopeId: value, scopeName: label } = scope;

              return {
                applied: false,
                count: null,
                groupIds: [],
                label,
                value
              };
            })
          };
        }
      }

      return catalogSearchFieldById[fieldId];
    })
    .filter(Boolean)
    .map(catalogSearchField => {
      const { id: fieldId, fieldFilters: filters, hasMore: fieldHasMore } = catalogSearchField;

      const isLanguageField = fieldId === FIELD_ID.LANGUAGE || fieldId === FIELD_ID.PRIMARY_LANGUAGE;
      if (isLanguageField && !renderAllLanguagesToggle) {
        throw new Error('language fields require a renderAllLanguagesToggle render function');
      }

      const hasShowMore = fieldId !== FIELD_ID.FORMAT && fieldHasMore;

      let isHidden = false;
      if (fieldId === FIELD_ID.LANGUAGE) {
        isHidden = !isIncludeAllLanguages;
      } else if (fieldId === FIELD_ID.PRIMARY_LANGUAGE) {
        isHidden = isIncludeAllLanguages;
      }

      const groupsById = filters.reduce((acc, fieldFilter) => {
        const { count: numResults, groupIds, label: filterLabel, value: filterId } = fieldFilter;
        const filtersType = FILTERS_TYPE_BY_FIELD_ID[fieldId];
        const isApplied = appliedFilterIdsByFieldId[fieldId]?.includes(filterId) ?? false;
        const isExclusive = filtersType === FILTERS_TYPE.RADIO_BUTTONS;

        (groupIds.length ? groupIds : [GROUP_ID.UNGROUPED]).forEach(groupId => {
          if (!acc[groupId]) {
            acc[groupId] = {
              filters: [],
              filtersType,
              groupId,
              label: formatGroupLabel({ fieldId, groupId })
            };
          }

          const isShown =
            groupId !== GROUP_ID.UNGROUPED ||
            isApplied ||
            acc[groupId].filters.length < MAX_NUM_SHOWN_FILTERS_IN_CONDENSED_VIEW;

          const filter = {
            filterId,
            fieldId,
            groupId,
            isApplied,
            isExclusive,
            isShown,
            label: formatFilterLabel({ fieldId, filterId, filterLabel, numResults })
          };

          acc[groupId].filters.push(filter);
        });

        return acc;
      }, {});

      const orderedGroupIdsByFieldId = {
        [FIELD_ID.FORMAT]: [
          GROUP_ID.BOOKS,
          GROUP_ID.AUDIOBOOKS_SPOKEN_WORD,
          GROUP_ID.MOVIES_TV_VIDEO,
          GROUP_ID.MUSIC_SOUND,
          GROUP_ID.GAMES_INTERACTIVE_MEDIA,
          GROUP_ID.JOURNALS_MAGAZINES,
          GROUP_ID.ACCESSIBLE_FORMATS,
          GROUP_ID.OTHER
        ]
      };

      const groups = (orderedGroupIdsByFieldId[fieldId] || Object.keys(groupsById))
        .map(groupId => groupsById[groupId])
        .filter(Boolean);

      // TODO: isInitiallyExpanded will need to be configurable for Search Page
      return {
        fieldId,
        hasMore: hasShowMore, // TODO: rename key
        isHidden,
        isInitiallyExpanded: isLanguageField && wasLanguageFieldToggled,
        isShowingMore: showMoreFieldId === fieldId,
        groups,
        label: formatFieldLabel({ fieldId }),
        numAppliedFilters: getNumAppliedFilters(fieldId, groups),
        renderContentBeforeGroups: isLanguageField ? renderAllLanguagesToggle : () => null,
        showMoreOverlayTitle: formatShowMoreOverlayTitle(fieldId)
      };
    });
};

// TODO: move
const mapCatalogSearchParamsToFacetFieldsSearchParams = catalogSearchParams => {
  return Array.from(catalogSearchParams.keys()).reduce((acc, catalogSearchKey) => {
    let key = catalogSearchKey;

    if (catalogSearchKey.startsWith(FILTER_PREFIX)) {
      key = key.substring(2).toLowerCase();

      catalogSearchParams
        .get(catalogSearchKey)
        .split(FILTER_SEPARATOR)
        .forEach(value => {
          acc.append(key, value);
        });
    } else {
      acc.set(key, catalogSearchParams.get(catalogSearchKey));
    }

    return acc;
  }, new URLSearchParams());
};

const mapQueryStateToCatalogSearchParams = (queryState, { includedFieldIds }) => {
  const { appliedFilterIdsByFieldId, isIncludeAllLanguages, page } = queryState;

  return includedFieldIds.reduce(
    (acc, fieldId) => {
      if (isIncludeAllLanguages && fieldId === FIELD_ID.PRIMARY_LANGUAGE) {
        return acc;
      }
      if (!isIncludeAllLanguages && fieldId === FIELD_ID.LANGUAGE) {
        return acc;
      }

      if (!appliedFilterIdsByFieldId[fieldId]?.length) {
        return acc;
      }

      if (fieldId === FIELD_ID.SCOPE) {
        acc.searchScope = appliedFilterIdsByFieldId[fieldId][0];
      } else {
        const serializedKey = FILTER_PREFIX.concat(fieldId.toUpperCase());

        acc[serializedKey] = appliedFilterIdsByFieldId[fieldId].join(FILTER_SEPARATOR);
      }

      return acc;
    },
    {
      page
    }
  );
};

const groupFiltersByIsApplied = filters => {
  return {
    applied: filters.filter(filter => filter.isApplied),
    unapplied: filters.filter(filter => !filter.isApplied)
  };
};

export {
  getUniqueAppliedFilters,
  groupFiltersByIsApplied,
  makeFieldLabel,
  makeFilterLabel,
  makeGroupLabel,
  makeTabLinkUrl,
  mapCategoriesToNavRowLinks,
  mapCatalogSearchFieldsToFacetFields,
  mapCatalogSearchParamsToFacetFieldsSearchParams,
  mapQueryStateToCatalogSearchParams
};
